/**
 * Title + description fields — Speaking-default markup (less border).
 */

import * as React from 'react';
import { Input, Label, Textarea } from '@ai-content-hub/content-kit';
import { cn } from '../lib/utils';

export interface CompositeEditTextField {
    id: string;
    label: React.ReactNode;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    /** EAA-2084: inline validation message shown under the input (red border + text). */
    error?: string;
}

export interface CompositeEditTitleDescriptionProps {
    className?: string;
    title: CompositeEditTextField;
    description: CompositeEditTextField & { rows?: number };
    disabled?: boolean;
    descriptionAsInput?: boolean;
    titleInputClassName?: string;
    descriptionInputClassName?: string;
}

export function CompositeEditTitleDescription({
    className,
    title,
    description,
    disabled = false,
    descriptionAsInput = false,
    titleInputClassName,
    descriptionInputClassName,
}: CompositeEditTitleDescriptionProps): React.ReactElement {
    const descRows = description.rows ?? 3;
    return (
        <div className={cn('space-y-6', className)}>
            <div className={title.error ? 'space-y-1.5' : 'space-y-2'}>
                <Label htmlFor={title.id}>
                    {title.label}
                    {title.required ? <span className="text-destructive"> *</span> : null}
                </Label>
                <Input
                    id={title.id}
                    value={title.value}
                    onChange={(e) => title.onChange(e.target.value)}
                    placeholder={title.placeholder}
                    disabled={disabled}
                    aria-invalid={title.error ? true : undefined}
                    aria-describedby={title.error ? `${title.id}-error` : undefined}
                    className={cn(
                        'text-lg font-semibold',
                        titleInputClassName,
                        title.error && 'border-destructive'
                    )}
                />
                {title.error ? (
                    <p
                        id={`${title.id}-error`}
                        role="alert"
                        className="composite-edit-title-error text-sm text-destructive"
                    >
                        {title.error}
                    </p>
                ) : null}
            </div>
            <div className="space-y-2">
                <Label htmlFor={description.id}>{description.label}</Label>
                {descriptionAsInput ? (
                    <Input
                        id={description.id}
                        value={description.value}
                        onChange={(e) => description.onChange(e.target.value)}
                        placeholder={description.placeholder}
                        disabled={disabled}
                        className={descriptionInputClassName}
                    />
                ) : (
                    <Textarea
                        id={description.id}
                        value={description.value}
                        onChange={(e) => description.onChange(e.target.value)}
                        placeholder={description.placeholder}
                        disabled={disabled}
                        rows={descRows}
                        className={descriptionInputClassName}
                    />
                )}
            </div>
        </div>
    );
}
